import { Monitor, Moon, Sun } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useTheme } from 'next-themes';
import styled from 'styled-components';
import { Button, Menu, MenuItem } from '@/components/ui';

export const THEME_OPTIONS = [
  { value: 'light', Icon: Sun },
  { value: 'dark', Icon: Moon },
  { value: 'system', Icon: Monitor },
];

/*
 * Both icons are always rendered and swapped by the .dark class in CSS, so the
 * server never has to know the theme (no flash, no hydration mismatch).
 */
const SunIcon = styled(Sun)`
  transform: rotate(0) scale(1);
  transition: transform 200ms;

  .dark & {
    transform: rotate(-90deg) scale(0);
  }
`;

const MoonIcon = styled(Moon)`
  position: absolute;
  transform: rotate(90deg) scale(0);
  transition: transform 200ms;

  .dark & {
    transform: rotate(0) scale(1);
  }
`;

/**
 * Light / dark / system switcher. Renders nothing on pages with a forced
 * theme (unsupported-platform), where a choice would have no visible effect.
 * Checkout hides the toggle via the navbar route check instead.
 */
const ThemeToggle = ({ align = 'end', className, side = 'bottom' }) => {
  const { forcedTheme, setTheme, theme } = useTheme();
  const t = useTranslations('theme');

  if (forcedTheme) return null;

  return (
    <Menu
      align={align}
      className={className}
      side={side}
      renderTrigger={(triggerProps) => (
        <Button
          $size="icon"
          $variant="ghost"
          aria-label={t('label')}
          title={t('label')}
          type="button"
          {...triggerProps}
        >
          <SunIcon size={20} strokeWidth={1.75} />
          <MoonIcon size={20} strokeWidth={1.75} />
        </Button>
      )}
    >
      {THEME_OPTIONS.map(({ value, Icon }) => (
        <MenuItem
          checked={theme === value}
          icon={<Icon size={16} strokeWidth={1.75} />}
          key={value}
          onSelect={() => setTheme(value)}
        >
          {t(value)}
        </MenuItem>
      ))}
    </Menu>
  );
};

export default ThemeToggle;
